<script setup lang="ts">
import { computed, useSlots } from 'vue';

import type { PhxPictureProps } from '@sg-core/src/View/Components/Atoms/Picture';

import { PhxsgButton, type PhxsgButtonProps } from '../../Atoms/Button';
import { PhxsgPicture } from '../../Atoms/Picture';

import '@sg-core/src/View/Components/Atoms/Box/box.css';
import '@sg-core/src/View/Components/Atoms/Overlay/overlay.css';
import '@sg-core/src/View/Components/Atoms/Text/text.css';
import '@sg/src/View/Components/Atoms/Overlay/overlay.css';
import '@sg/src/View/Components/Atoms/RichText/rich-text.css';

export interface PhxsgPagePushProps {
  title?: string;
  titleTag?: string;
  media?: PhxPictureProps;
  background?: PhxPictureProps;
  overlay?: boolean;
  stacked?: boolean;
  description?: string;
  clampedDescription?: boolean;
  cta?: PhxsgButtonProps & { text?: string };
}

const {
  title,
  titleTag = 'h3',
  media,
  background,
  overlay = false,
  stacked = false,
  description,
  clampedDescription = false,
  cta,
} = defineProps<PhxsgPagePushProps>();

const slots = useSlots();

const hasBackground = computed(() => !!background || !!slots.background);
const hasMedia = computed(() => !!media || !!slots.media);
const hasTitle = computed(() => !!title || !!slots.title);
const hasCta = computed(() => !!cta || !!slots.cta);

const descriptionClasses = computed(() => [
  'phxsg-rich-text',
  'phxsg-rich-text--tight',
  'phxsg-page-push__rich-text',
  { 'phxsg-page-push__rich-text--clamped': clampedDescription },
]);

const ctaAttrs = computed(() => {
  if (!cta) {
    return {};
  }

  const { text, ...attrs } = cta;

  return attrs;
});
</script>

<template>
  <article :class="['phxsg-page-push', 'phxsg-box', { 'phxsg-page-push--stacked': stacked }]">
    <div v-if="hasBackground" class="phxsg-page-push__background">
      <slot name="background">
        <PhxsgPicture v-if="background" v-bind="background" :type="background.type ?? 'cover'" />
      </slot>
    </div>

    <div v-if="overlay" class="phxsg-overlay phxsg-overlay--negative"></div>

    <figure v-if="hasMedia" class="phxsg-page-push__media">
      <slot name="media">
        <PhxsgPicture v-if="media" v-bind="media" :type="media.type ?? 'contain'" />
      </slot>
    </figure>

    <div class="phxsg-page-push__body">
      <component :is="titleTag" v-if="hasTitle" class="phxsg-page-push__title body body--weight-medium">
        <slot name="title">{{ title }}</slot>
      </component>

      <div v-if="slots.description" :class="descriptionClasses">
        <slot name="description" />
      </div>

      <!-- eslint-disable-next-line vue/no-v-html -->
      <div v-else-if="description" :class="descriptionClasses" v-html="description"></div>

      <slot v-if="hasCta" name="cta" :cta="cta">
        <PhxsgButton v-if="cta" use="a" variant="fade" class="expand-target phxsg-page-push__cta" v-bind="ctaAttrs">
          {{ cta.text }}
        </PhxsgButton>
      </slot>
    </div>
  </article>
</template>

<style src="./page-push.css" />
